<template>
  <v-form
    ref="form"
    v-model="formValid"
    class="d-flex flex-column align-center mb-16"
    :style="formStyle"
    lazy-validation
    @submit.prevent="handleFormSubmit"
  >
    <div class="form-content-wrapper mt-0 p-0">
      <div class="d-flex flex-column align-center w-100 mb-16">
        <span class="text-h5 text-uppercase mb-12 text--allout-grey">
          {{ $t('action.panels.FUNDRAISER_INITIAL.heading', locale) }}
        </span>
        <v-btn-toggle v-model="frequencyIndex" class="w-100" mandatory>
          <v-btn
            color="allout-blue"
            :class="`font-weight-regular text-lowercase flex-grow-1 ${
              fundraiserUI.selectedPaymentFrequency === 'one_time'
                ? 'font-weight-bold white--text'
                : ''
            }`"
            :outlined="fundraiserUI.selectedPaymentFrequency !== 'one_time'"
            small
          >
            {{ $t('action.panels.FUNDRAISER_INITIAL.buttons.one-time.label', locale) }}
          </v-btn>
          <v-btn
            color="allout-blue"
            :class="`font-weight-regular text-lowercase flex-grow-1 ${
              fundraiserUI.selectedPaymentFrequency === 'monthly'
                ? 'font-weight-bold white--text'
                : ''
            }`"
            :outlined="fundraiserUI.selectedPaymentFrequency !== 'monthly'"
            small
          >
            {{ $t('action.panels.FUNDRAISER_INITIAL.buttons.monthly.label', locale) }}
          </v-btn>
        </v-btn-toggle>
      </div>
      <div class="d-flex justify-center flex-wrap" style="margin-right: -16px">
        <v-btn
          v-for="amount in donationAmountsWithInserted"
          :key="amount"
          :outlined="fundraiserUI.amount !== amount"
          :class="`mr-4 mb-4 ${
            fundraiserUI.amount === amount ? 'font-weight-bold white--text' : ''
          }`"
          color="allout-grey"
          min-width="110"
          max-width="110"
          height="44"
          large
          full-width
          @click="handleAmountButtonClicked(amount)"
        >
          {{ currentCurrencyConfig.symbol }}{{ amount }}
        </v-btn>
      </div>
      <div class="d-flex flex-column align-center w-100" style="max-width: 362px">
        <v-text-field
          ref="amountInput"
          type="number"
          height="44"
          class="mb-8 w-100"
          color="allout-grey"
          :value="fundraiserUI.amount"
          :placeholder="
            $t('action.panels.FUNDRAISER_INITIAL.fields.amountInput.placeholder', locale)
          "
          :rules="amountRules"
          outlined
          required
          @input="handleAmountInput"
        >
          <template #prepend-inner>
            <div class="d-flex flex-column align-center">
              {{ currentCurrencyConfig.symbol }}
            </div>
          </template>
          <template #append-outer>
            <label class="d-flex" for="currency-select">
              <v-btn
                ref="currencySelectButton"
                class="allout-grey--text ml-4"
                color="allout-lightgrey"
                elevation="0"
                height="44"
                large
              >
                {{ currentCurrencyConfig.code }}
                <v-icon>{{ icons.mdiMenuDown }}</v-icon>
              </v-btn>
              <select
                id="currency-select"
                ref="currencySelect"
                name="currency"
                @change="handleCurrencyChange"
              >
                <optgroup v-for="(optGroup, i) in currencySelectOptGroups" :key="`optgroup-${i}`">
                  <option
                    v-for="option in optGroup"
                    :key="option[0]"
                    :value="option[0]"
                    :selected="option[0] === currentCurrencyConfig.code"
                  >
                    {{ option[1] }}
                  </option>
                </optgroup>
              </select>
            </label>
          </template>
        </v-text-field>
      </div>
      <v-btn
        type="submit"
        color="secondary"
        class="text-uppercase font-weight-bold w-100 donate-submit-btn"
        :disabled="!formValid"
        large
        @click="handleDonateButtonClicked"
      >
        {{
          $t(
            fundraiserUI.selectedPaymentFrequency === 'one_time'
              ? 'action.panels.FUNDRAISER_INITIAL.buttons.donate.label.one-time'
              : 'action.panels.FUNDRAISER_INITIAL.buttons.donate.label.monthly',
            locale
          )
        }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { mdiMenuDown } from '@mdi/js'

export default {
  name: 'PanelFundraiserInitial',
  props: {
    formStyle: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data: vm => ({
    buttonsMaxWidth: 380,
    icons: {
      mdiMenuDown
    },
    formValid: true,
    frequencyIndex: 0,
    amountRules: [
      v => !!v || vm.$t('action.panels.FUNDRAISER_INITIAL.fields.amountInput.required', vm.locale),
      v => {
        const amount = Number(vm.fundraiserUI.amount)
        return (
          !isNaN(amount) ||
          amount % 1 === 0 ||
          vm.$t('action.panels.FUNDRAISER_INITIAL.fields.amountInput.number', vm.locale)
        )
      },
      v => {
        const { minimumDonationAmount, currentCurrencyConfig } = vm
        const { symbol } = currentCurrencyConfig
        const amount = Number(v)
        return (
          amount >= vm.minimumDonationAmount ||
          vm.$t('action.panels.FUNDRAISER_INITIAL.fields.amountInput.minimum', vm.locale, {
            symbol,
            minimumDonationAmount
          })
        )
      }
    ]
  }),
  computed: {
    ...mapState('actionForm', ['fundraiserUI', 'formConfig', 'locale']),
    ...mapGetters('actionForm', [
      'currentCurrencyConfig',
      'donationAmountsWithInserted',
      'currencySelectOptGroups',
      'minimumDonationAmount'
    ])
  },
  watch: {
    frequencyIndex(value) {
      // Do this every time the payment frequency index changes
      switch (value) {
        case 0:
          return this.$store.dispatch('actionForm/updatePaymentFrequency', {
            selectedPaymentFrequency: 'one_time'
          })
        case 1:
          return this.$store.dispatch('actionForm/updatePaymentFrequency', {
            selectedPaymentFrequency: 'monthly'
          })
        default:
          throw new Error(`Unhandled payment frequency index "${value}"`)
      }
    }
  },
  mounted() {
    switch (this.fundraiserUI.selectedPaymentFrequency) {
      case 'one_time':
        return (this.frequencyIndex = 0)
      case 'monthly':
        return (this.frequencyIndex = 1)
      default:
        throw new Error(
          `Unhandled payment frequency "${this.fundraiserUI.selectedPaymentFrequency}"`
        )
    }
  },
  methods: {
    handleAmountButtonClicked(amount) {
      this.$store.commit('actionForm/UPDATE_FUNDRAISER_UI', {
        amount,
        amountSelectionIsDirty: true
      })
      this.$refs.amountInput.focus()
    },
    handleCurrencyChange(evt) {
      this.$store.commit('actionForm/UPDATE_FUNDRAISER_UI', {
        currency: evt.target.value
      })
    },
    handleAmountInput(amount) {
      this.$store.commit('actionForm/UPDATE_FUNDRAISER_UI', {
        amount
      })
    },
    handleDonateButtonClicked() {
      this.$refs.form.validate()
    },
    handleFormSubmit() {
      if (this.formValid) {
        this.$store.dispatch('actionForm/changeToNextPanel')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.form-content-wrapper {
  max-width: 236px;
}
@media screen and (max-width: 462px) {
  .donate-submit-btn {
    font-size: inherit;
  }
}
.v-btn::before {
  background-color: transparent;
}
::v-deep {
  .v-text-field {
    > .v-input__control {
      > .v-input__slot {
        // This line needs to be adjusted if field height changes. There was no better way to do this :/
        min-height: 44px !important;
        font-size: 1.5rem;

        > .v-text-field__slot {
          margin: 0 !important;
          padding: 0 !important;
        }

        > .v-input__prepend-inner {
          height: 100%;
          align-items: center;
          margin-top: 0;
          font-size: 1.1rem;
        }

        input {
          color: #04a7cb;
          padding: 0;
          font-weight: 600;
          position: relative;

          &::placeholder {
            font-size: 1.1rem;
            font-weight: 400;
            position: absolute;
            top: 4px;
          }
        }
      }
    }
    .v-input__append-outer {
      margin: 0;
      position: relative;
      label {
        display: block;
        button:hover {
          cursor: pointer;
        }
      }
    }
  }
}
#currency-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  -ms-appearance: none;
  appearance: none;
  opacity: 0.0001;

  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

@media screen and (min-width: 462px) {
  .form-content-wrapper {
    max-width: 362px;
  }
}
</style>
